import { useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

type SwipeDirection = 'left' | 'right' | 'up' | 'down';

/**
 * Ordine delle tab nella bottom-nav.
 * Lo swipe left → avanza di una tab, right → torna indietro.
 */
const TAB_ROUTES = [
  '/dashboard',
  '/events',
  '/nearby',
  '/chats',
  '/garage',
] as const;

interface UseSwipeNavigationOptions {
  /** Callback custom al rilevamento dello swipe (opzionale) */
  onSwipe?: (direction: SwipeDirection) => void;
  /** Pixel minimi per contare come swipe (default: 50) */
  threshold?: number;
  /** Velocità minima px/ms per contare come swipe (default: 0.2) */
  velocityThreshold?: number;
  /** Se false il gesto viene ignorato (default: true) */
  enabled?: boolean;
  /** Ref al contenitore; usa window se omesso */
  containerRef?: React.RefObject<HTMLElement>;
}

/**
 * useSwipeNavigation
 *
 * Rileva gesti di swipe orizzontale e:
 *  1. Naviga tra le tab (left/right) tramite React Router
 *  2. Chiama onSwipe(direction) se fornito
 *
 * Aggiunge il controllo velocità (velocityThreshold) e il flag enabled
 * richiesti da Layout.tsx.
 *
 * Canonical path: import { useSwipeNavigation } from '@/hooks/infrastructure/useSwipeNavigation';
 */
export function useSwipeNavigation({
  onSwipe,
  threshold         = 50,
  velocityThreshold = 0.2,
  enabled           = true,
  containerRef,
}: UseSwipeNavigationOptions = {}): void {
  const navigate  = useNavigate();
  const startRef  = useRef<{ x: number; y: number; t: number } | null>(null);

  const getTarget = useCallback(
    () => (containerRef?.current ?? window) as EventTarget,
    [containerRef]
  );

  const handleTouchStart = useCallback((e: Event) => {
    if (!enabled) return;
    const touch = (e as TouchEvent).touches[0];
    startRef.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
  }, [enabled]);

  const handleTouchEnd = useCallback((e: Event) => {
    if (!enabled || !startRef.current) return;

    const touch    = (e as TouchEvent).changedTouches[0];
    const dx       = touch.clientX - startRef.current.x;
    const dy       = touch.clientY - startRef.current.y;
    const dt       = Date.now() - startRef.current.t;
    startRef.current = null;

    const absDx   = Math.abs(dx);
    const absDy   = Math.abs(dy);
    const velocity = absDx / dt;          // px/ms

    // Ignora se non supera soglia distanza o velocità, o se prevalentemente verticale
    if (absDx < threshold || velocity < velocityThreshold || absDy > absDx) return;

    const direction: SwipeDirection = dx > 0 ? 'right' : 'left';

    // Naviga tra le tab
    const currentPath = window.location.pathname;
    const idx = TAB_ROUTES.findIndex((r) => currentPath.startsWith(r));
    if (idx !== -1) {
      if (direction === 'left'  && idx < TAB_ROUTES.length - 1) navigate(TAB_ROUTES[idx + 1]);
      if (direction === 'right' && idx > 0)                      navigate(TAB_ROUTES[idx - 1]);
    }

    onSwipe?.(direction);
  }, [enabled, threshold, velocityThreshold, navigate, onSwipe]);

  useEffect(() => {
    const target = getTarget();
    target.addEventListener('touchstart', handleTouchStart, { passive: true });
    target.addEventListener('touchend',   handleTouchEnd,   { passive: true });
    return () => {
      target.removeEventListener('touchstart', handleTouchStart);
      target.removeEventListener('touchend',   handleTouchEnd);
    };
  }, [getTarget, handleTouchStart, handleTouchEnd]);
}
